import React, { useEffect, useRef, useState } from 'react';
import {
  avatarCrop,
  encodeAvatar,
  loadAvatarImage,
  validateAvatarDimensions,
} from './avatar-image';

export function AvatarEditor({
  file,
  busy,
  onSave,
}: {
  file: File;
  busy: boolean;
  onSave: (file: File) => Promise<void>;
}) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0.5, y: 0.5 });
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(false);
  const [encoding, setEncoding] = useState(false);
  const drag = useRef<{ x: number; y: number; position: typeof position } | null>(null);
  const prepared = useRef<File | null>(null);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    let current = true;
    const url = URL.createObjectURL(file);
    loadAvatarImage(url)
      .then((value) => {
        validateAvatarDimensions(value.naturalWidth, value.naturalHeight);
        if (current) setImage(value);
      })
      .catch((reason: Error) => {
        if (current) setError(reason.message);
      });
    return () => {
      current = false;
      active.current = false;
      URL.revokeObjectURL(url);
    };
  }, [file]);
  const crop =
    image && avatarCrop(image.naturalWidth, image.naturalHeight, zoom, position.x, position.y);
  const disabled = busy || encoding;
  function adjust(next: typeof position, nextZoom = zoom) {
    prepared.current = null;
    setError('');
    setRetry(false);
    setPosition(next);
    setZoom(nextZoom);
  }
  async function save() {
    if (!image || !crop || disabled) return;
    setError('');
    setEncoding(true);
    try {
      prepared.current ||= await encodeAvatar(image, crop);
      if (active.current) await onSave(prepared.current);
    } catch (reason) {
      if (!active.current) return;
      setError(reason instanceof Error ? reason.message : '头像上传失败，请重试');
      setRetry(!!prepared.current);
    } finally {
      if (active.current) setEncoding(false);
    }
  }
  return (
    <div className="avatar-editor">
      {image && crop ? (
        <div
          className="avatar-crop-area"
          role="group"
          aria-label="头像裁剪预览，拖动图片或使用方向键调整位置"
          tabIndex={0}
          onPointerDown={(event) => {
            if (disabled) return;
            event.currentTarget.setPointerCapture(event.pointerId);
            drag.current = { x: event.clientX, y: event.clientY, position };
          }}
          onPointerMove={(event) => {
            if (!drag.current || disabled) return;
            const ratio = event.currentTarget.getBoundingClientRect().width / crop.side;
            const dx = (image.naturalWidth - crop.side) * ratio;
            const dy = (image.naturalHeight - crop.side) * ratio;
            adjust({
              x: dx
                ? Math.max(
                    0,
                    Math.min(1, drag.current.position.x - (event.clientX - drag.current.x) / dx),
                  )
                : 0.5,
              y: dy
                ? Math.max(
                    0,
                    Math.min(1, drag.current.position.y - (event.clientY - drag.current.y) / dy),
                  )
                : 0.5,
            });
          }}
          onPointerUp={() => {
            drag.current = null;
          }}
          onPointerCancel={() => {
            drag.current = null;
          }}
          onKeyDown={(event) => {
            if (
              disabled ||
              !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)
            )
              return;
            event.preventDefault();
            adjust({
              x: Math.max(
                0,
                Math.min(
                  1,
                  position.x +
                    (event.key === 'ArrowRight' ? 0.05 : event.key === 'ArrowLeft' ? -0.05 : 0),
                ),
              ),
              y: Math.max(
                0,
                Math.min(
                  1,
                  position.y +
                    (event.key === 'ArrowDown' ? 0.05 : event.key === 'ArrowUp' ? -0.05 : 0),
                ),
              ),
            });
          }}
        >
          <img
            src={image.src}
            alt="待上传的头像"
            draggable={false}
            style={{
              width: `${(image.naturalWidth / crop.side) * 100}%`,
              height: `${(image.naturalHeight / crop.side) * 100}%`,
              left: `${(-crop.x / crop.side) * 100}%`,
              top: `${(-crop.y / crop.side) * 100}%`,
            }}
          />
          <span className="avatar-crop-guide" aria-hidden="true" />
        </div>
      ) : (
        !error && (
          <p className="muted" role="status">
            正在读取图片…
          </p>
        )
      )}
      <label className="avatar-zoom">
        缩放
        <input
          aria-label="头像缩放"
          type="range"
          min="1"
          max="3"
          step="0.01"
          value={zoom}
          disabled={!image || disabled}
          onChange={(event) => adjust(position, Number(event.target.value))}
        />
      </label>
      {error && (
        <p className="text-danger" role="alert">
          {error}
        </p>
      )}
      <div className="row spread">
        <button
          type="button"
          className="btn btn-ghost"
          disabled={!image || disabled}
          onClick={() => adjust({ x: 0.5, y: 0.5 }, 1)}
        >
          重置裁剪
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!image || disabled}
          onClick={() => void save()}
        >
          {disabled ? '正在处理并上传…' : retry ? '重新上传' : '确认并上传'}
        </button>
      </div>
    </div>
  );
}
